// src/components/ui/TaskTableView.js
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import BadgeDropdown from './BadgeDropdown';
import CustomDropdown from './CustomDropdown';
import { sendNotificationToAllUsers } from '../../utils/supabaseHelpers';
import { useRealtimeTables } from '../../hooks/useRealtimeTables';
import { getPriorityTextClass, formatPriorityLabel } from '../../utils/priorityColors';

// --- Badge Styling Helper ---
const getBadgeClass = (type, value) => {
  if (!value) return 'text-gray-400';

  const normalized = value.toLowerCase();

  if (type === 'priority') {
    return getPriorityTextClass(value);
  }
  if (type === 'domain') {
    return 'text-gray-300';
  }
  if (type === 'status') {
    return 'text-gray-300';
  }
  return 'text-gray-400';
};

// --- Domain Formatter ---
const formatDomain = (domain) => {
  if (!domain) return 'N/A';
  return domain
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// --- Capitalize Helper ---
const capitalize = (str) => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : 'N/A';

const TaskTableView = ({ problems = [], onRefresh, onUpdateTask }) => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // --- State for Click-to-Edit ---
  // Will store { rowId: '...', column: 'status' | 'assignee' | 'deadline' }
  const [editingCell, setEditingCell] = useState(null);

  const navigate = useNavigate();

  // Convert problems to tasks format for compatibility
  const tasks = problems.map(problem => ({
    id: problem.id,
    title: problem.title,
    status: problem.status,
    domain: problem.domain,
    priority: problem.priority,
    deadline_date: problem.due_date, // Map due_date to deadline_date
    assigned_user: problem.assigned_user || null,
    assigned_to: problem.assigned_to, // Ensure UUID is passed down
    assignee_name: problem.assignee_name,
    type: problem.type || 'problem', // Default to problem if undefined
    parentId: problem.parentId || null
  }));

  // Show subtasks; only show main task if it has no subtasks
  const subtasks = tasks.filter(task => task.type === 'subtask');
  const parentsWithSubtasks = new Set(subtasks.map(task => task.parentId).filter(Boolean));
  const visibleTasks = tasks.filter(task => task.type === 'subtask' || !parentsWithSubtasks.has(task.id));

  // Split subtask title into parent and subtask parts for styling
  const parseSubtaskTitle = (title) => {
    if (!title || !title.startsWith('@')) return { parent: null, sub: title };

    const stripped = title.slice(1).trim();
    const parts = stripped.split(' - ');
    if (parts.length === 1) return { parent: parts[0], sub: '' };

    const parent = parts.shift().trim();
    const sub = parts.join(' - ').trim();
    return { parent, sub };
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true);

      // Only fetch users data - problems data comes from props
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, name, is_active, inactive_user_name');

      if (userError) {
        console.error('Error fetching users:', userError);
        setError(userError.message);
      } else {
        setUsers(userData);
      }

      setLoading(false);
    };

    fetchUsers();
  }, []); // Only fetch users once

  useRealtimeTables({
    channelName: 'task-table-users',
    tables: ['users'],
    onChange: async () => {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, name, is_active, inactive_user_name');

      if (!userError) {
        setUsers(userData || []);
      }
    }
  });

  // --- UPDATED HANDLERS ---
  const handleUpdateStatus = async (problemId, newStatus) => {
    setEditingCell(null); // Close editor

    // Find the task to check its type
    const task = tasks.find(t => t.id === problemId);

    if (task && task.type === 'subtask') {
      // For subtasks, map status to is_completed boolean
      const isCompleted = newStatus === 'Done' || newStatus === 'Completed';

      const { error } = await supabase
        .from('subtasks')
        .update({ is_completed: isCompleted })
        .eq('id', problemId);

      if (error) {
        console.error('Error updating subtask status:', error);
        alert('Failed to update subtask status.');
      } else if (onRefresh) {
        const { parent, sub } = parseSubtaskTitle(task.title);
        try {
          await sendNotificationToAllUsers({
            type: 'task',
            referenceId: task.parentId || problemId,
            title: `${isCompleted ? 'Subtask Completed: ' : 'Subtask Reopened: '}${parent ? `${parent} - ${sub}` : task.title}`,
            message: `In task: ${parent || 'Task'}`,
            link: '/tasks'
          });
        } catch (notificationError) {
          console.error('Error creating subtask status notification:', notificationError);
        }
        onRefresh();
      }
      return;
    }

    // If parent provided onUpdateTask, use it (preferred method for main tasks)
    if (onUpdateTask) {
      try {
        await onUpdateTask(problemId, newStatus);
        // onUpdateTask will handle refresh via loadProblems
      } catch (error) {
        console.error('Error updating status:', error);
      }
      return;
    }

    // Fallback: Direct database update (if onUpdateTask not provided)
    const { error } = await supabase
      .from('problems')
      .update({ status: newStatus })
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to update status.');
    } else if (onRefresh) {
      // Refresh data after successful update
      onRefresh();
    }
  };

  const handleAssignUser = async (problemId, newUserId) => {
    console.log('handleAssignUser called:', { problemId, newUserId });
    setEditingCell(null); // Close editor
    const userIdToSet = newUserId === "" ? null : newUserId;

    // Find the task to check its type
    const task = tasks.find(t => t.id === problemId);
    const table = (task && task.type === 'subtask') ? 'subtasks' : 'problems';

    const selectedUser = users.find(u => u.id === userIdToSet);
    const selectedUserName = selectedUser
      ? (selectedUser.name || selectedUser.email || null)
      : null;

    const updatePayload = table === 'problems'
      ? { assigned_to: userIdToSet, assignee_name: selectedUserName }
      : { assigned_to: userIdToSet };

    console.log(`Updating ${table} with userIdToSet:`, userIdToSet);
    const { data: updatedRow, error } = await supabase
      .from(table)
      .update(updatePayload)
      .eq('id', problemId)
      .select('id, assigned_to')
      .maybeSingle();

    if (error) {
      console.error('Error updating assignment:', error);
      alert('Failed to assign user.');
    } else if (!updatedRow) {
      console.error('Assignment update affected 0 rows. Likely RLS restriction or row not found.');
      alert('Assignment was blocked by permissions. Please run the collaboration RLS migration.');
    } else {
      console.log('Successfully updated assignment');

      if (userIdToSet) {
        try {
          const displayName = getAssigneeDisplayName({ assigned_to: userIdToSet });
          const notificationTitle = task && task.type === 'subtask'
            ? (() => {
                const { parent, sub } = parseSubtaskTitle(task.title);
                return `${parent || 'Task'} - ${sub || task.title} assigned to ${displayName || 'someone'}`;
              })()
            : `${task?.title || 'Task'} assigned to ${displayName || 'someone'}`;

          await sendNotificationToAllUsers({
            type: 'task',
            referenceId: task?.type === 'subtask' ? (task.parentId || problemId) : problemId,
            title: notificationTitle,
            message: task?.type === 'subtask' ? 'Subtask Assigned' : 'Task Assigned',
            link: '/tasks'
          });
        } catch (notificationError) {
          console.error('Error creating assignment notification:', notificationError);
        }
      }

      if (onRefresh) {
        await onRefresh();
      }
    }
  };

  const handleSetDeadline = async (problemId, newDate) => {
    setEditingCell(null); // Close editor

    // Find the task to check its type
    const task = tasks.find(t => t.id === problemId);
    const table = (task && task.type === 'subtask') ? 'subtasks' : 'problems';
    const dateField = (task && task.type === 'subtask') ? 'due_date' : 'deadline_date'; // subtasks use due_date, problems use deadline_date (or due_date?)
    // Wait, problems table schema says due_date AND deadline_date. 
    // In Dashboard.js createProblem uses due_date. 
    // In TaskTableView mapping: deadline_date: problem.due_date.
    // So for problems, we should update due_date?
    // Let's check handleSetDeadline original implementation.
    // It updated 'deadline_date'. 
    // But Dashboard.js maps due_date to deadline_date.
    // If I update 'deadline_date' in DB, but Dashboard reads 'due_date', it might not show up.
    // The user schema shows both due_date and deadline_date.
    // I'll stick to 'due_date' for subtasks and 'due_date' for problems if that's what Dashboard uses.
    // Dashboard.js: due_date: problemData.dueDate || null.
    // So 'due_date' is the main field.
    // But TaskTableView original code updated 'deadline_date'. This might have been a bug or intentional.
    // I will update 'due_date' for both to be safe and consistent with Dashboard.

    const { error } = await supabase
      .from(table)
      .update({ due_date: newDate }) // Changed to due_date for consistency
      .eq('id', problemId);

    if (error) {
      console.error('Error:', error);
      alert('Failed to set deadline.');
    } else if (onRefresh) {
      onRefresh();
    }
  };

  const handleTitleClick = (problemId) => {
    // If it's a subtask, open the parent task instead
    const task = tasks.find(t => t.id === problemId);
    let taskIdToOpen = problemId;
    
    if (task && task.type === 'subtask' && task.parentId) {
      // Open the parent task for subtasks
      taskIdToOpen = task.parentId;
    }
    
    navigate('/tasks', { state: { openProblemId: taskIdToOpen } });
  };

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-500">Error: {error}</div>;

  // --- Helper to get display name for assignee ---
  const getAssigneeDisplayName = (task) => {
    if (!task.assigned_to) return null;

    if (task.assigned_user) {
      const isInactive = task.assigned_user.is_active === false;
      return isInactive && task.assigned_user.inactive_user_name
        ? task.assigned_user.inactive_user_name
        : (task.assigned_user.name || task.assigned_user.email || task.assignee_name || null);
    }

    if (task.assignee_name) {
      return task.assignee_name;
    }
    
    const assignedUser = users.find(u => u.id === task.assigned_to);
    if (!assignedUser) return task.assignee_name;
    
    const isInactive = assignedUser.is_active === false;
    return isInactive && assignedUser.inactive_user_name 
      ? assignedUser.inactive_user_name 
      : (assignedUser.name || assignedUser.email);
  };

  // --- Helper to render the styled badge/text ---
  const renderCellContent = (task, column) => {
    if (column === 'status') {
      return (
        <span className={`text-sm ${getBadgeClass('status', task.status)}`}>
          {capitalize(task.status)}
        </span>
      );
    }
    if (column === 'assignee') {
      const displayName = getAssigneeDisplayName(task);
      return (
        <span className="text-gray-200">
          {displayName || <span className="text-gray-400">Unassigned</span>}
        </span>
      );
    }
    if (column === 'deadline') {
      return (
        <span className="text-gray-200">
          {task.deadline_date ? format(new Date(task.deadline_date), 'MMM dd, yyyy') : <span className="text-gray-400">No deadline</span>}
        </span>
      );
    }
  };

  // --- Main component render ---
  return (
    <div className="p-6 max-w-7xl mx-auto">

      {/* --- Table --- */}
      <div className="glass-card overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-600">
          <thead className="bg-gray-800/50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Problem Title
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Domain
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Assigned To
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-300 uppercase tracking-wider">
                Deadline
              </th>
            </tr>
          </thead>
          <tbody className="bg-gray-900/20 divide-y divide-gray-600">
            {visibleTasks.length === 0 && (
              <tr>
                <td colSpan="6" className="px-6 py-4 text-center text-gray-400">
                  No tasks found.
                </td>
              </tr>
            )}
            {visibleTasks.map((task) => (
              <tr key={task.id} className="hover:bg-gray-800/30 transition-colors">
                {/* Title Cell (Link) */}
                <td className="px-6 py-4 align-top">
                  <div
                    className="text-sm font-medium hover:underline cursor-pointer break-words whitespace-normal leading-snug"
                    onClick={() => handleTitleClick(task.id)}
                    title={task.title}
                  >
                    {task.type === 'subtask' ? (() => {
                      const { parent, sub } = parseSubtaskTitle(task.title);
                      return (
                        <>
                          {parent && <span className="text-indigo-400">{parent}</span>}
                          {sub && <span className="text-gray-100">{` - ${sub}`}</span>}
                          {!parent && !sub && <span className="text-gray-100">{task.title}</span>}
                        </>
                      );
                    })() : (
                      <span className="text-gray-100 hover:text-white">{task.title}</span>
                    )}
                  </div>
                </td>

                {/* Status Cell (Click-to-Edit) */}
                <td
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50"
                  onClick={() => setEditingCell({ rowId: task.id, column: 'status' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'status' ? (
                    <div className="relative z-10">
                      <BadgeDropdown
                        value={task.status}
                        onChange={(value) => handleUpdateStatus(task.id, value)}
                        type="status"
                        options={[
                          { value: 'Not Decided', label: 'Not Decided' },
                          { value: 'In Progress', label: 'In Progress' },
                          { value: 'Hold', label: 'Hold' },
                          { value: 'Done', label: 'Done' }
                        ]}
                        className="min-w-[140px]"
                      />
                    </div>
                  ) : (
                    renderCellContent(task, 'status')
                  )}
                </td>

                {/* Domain Cell (Static) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`text-sm ${getBadgeClass('domain', task.domain)}`}>
                    {formatDomain(task.domain)}
                  </span>
                </td>

                {/* Priority Cell (Static) */}
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`text-sm ${getBadgeClass('priority', task.priority)}`}>
                    {formatPriorityLabel(task.priority)}
                  </span>
                </td>

                {/* Assignee Cell (Click-to-Edit) */}
                <td
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50"
                  onClick={() => setEditingCell({ rowId: task.id, column: 'assignee' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'assignee' ? (
                    <div className="relative z-10">
                      <CustomDropdown
                        value={task.assigned_to || ''}
                        onChange={(value) => {
                          console.log('CustomDropdown onChange triggered:', { taskId: task.id, selectedValue: value });
                          handleAssignUser(task.id, value);
                        }}
                        options={[
                          { value: '', label: 'Unassigned' },
                          ...users
                            .filter(user => user.is_active !== false)
                            .map(user => {
                              return { 
                                value: user.id, 
                                label: user.name || user.email 
                              };
                            })
                        ]}
                        className="min-w-[160px]"
                      />
                    </div>
                  ) : (
                    renderCellContent(task, 'assignee')
                  )}
                </td>

                {/* Deadline Cell (Click-to-Edit) */}
                <td
                  className="px-6 py-4 whitespace-nowrap cursor-pointer hover:bg-gray-800/50"
                  onClick={() => setEditingCell({ rowId: task.id, column: 'deadline' })}
                >
                  {editingCell?.rowId === task.id && editingCell?.column === 'deadline' ? (
                    <input
                      type="date"
                      autoFocus
                      value={task.deadline_date || ''}
                      onBlur={() => setEditingCell(null)}
                      onChange={(e) => handleSetDeadline(task.id, e.target.value)}
                      className="glass-input text-sm"
                    />
                  ) : (
                    renderCellContent(task, 'deadline')
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default TaskTableView;